import api from './api';
import { create } from 'fontkit';
import { toast } from 'react-toastify';
import { deleteFromIndexedDB } from './indexedDB';
import { checkUnsupportedGlyphs } from './utils/fontHelpers';

export const getError = (error) => {
  if (error.response && error.response.data.message) {
    const message = error.response.data.message;
    const match = message.match(/error-key: ([\w-]+)/); // checks if the error message contains an error key in the format 'error-key'. I use this key to extract the useTranslation identifier
    return match ? match[1] : message; // If the match is found, return the error key, otherwise return the original message
  }
  return error.message; // If there is no response or error message, return the general error message
};

export const materialSaveError = (err) =>
  err?.response?.status === 409
    ? 'Die Bestandswerte haben sich zwischenzeitlich geändert. Bitte Seite aktualisieren und erneut versuchen.'
    : getError(err); // what the toast says when a material edit screen's save fails: the 409 of the material routes means another save changed the document's quantities since the screen loaded them (stockQuantitiesBody in utils/stockAdjustments.js), anything else shows what the request says (the seven screens held a copy each until chain 9, Oct 2026)

export const toastRequestError = (err, t) =>
  toast.error(t(getError(err), { details: err?.response?.data?.details })); // shows what a failed request says: the route's key translated, with the details some keys print, else the message the error carries (every axios error has one); the customizer's uploaded-icon caches and access check toast through it (each held a copy until a PR 31 review)

export const formatEuro = (amount) => {
  const locale = navigator.language || 'en-US'; // Get user's locale from their browser (geolocation-based)

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currencyDisplay: 'symbol',
  }).format(amount);
};

export const formatDate = (
  dateInput,
  options = {
    showTime: false,
    showTimeZone: false,
    dateOnly: false,
  }
) => {
  if (!dateInput) {
    return '';
  }

  const locale = options.locale || navigator.language || 'en-US'; // the caller's locale when it passes one (AdminDashboardScreen's holiday dates, German like its sales chart), else the user's browser locale

  // calendar dates such as "2026-09-21" must not be shifted according to the user's timezone:
  if (options.dateOnly) {
    const [year, month, day] = dateInput.split('-').map(Number);

    const date = new Date(Date.UTC(year, month - 1, day));

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    }).format(date);
  }

  // normal timestamps are displayed in the user's local timezone:
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; // Get user's timezone from their system
  const date = new Date(dateInput); // Convert input to a Date object

  const formatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }; // Define formatting options

  if (options.showTime) {
    // If time should be included, add hours, minutes, and seconds
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
    formatOptions.second = '2-digit';
  }

  if (options.showTimeZone) {
    // If timezone label should be included, add timeZoneName
    formatOptions.timeZoneName = 'short';
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
};

export async function checkAndReplaceInactiveFonts({
  designsData, // Can be a single design object or an array
  fallbackFontFamilyName = 'notosans',
  fallbackFontName = 'NotoSans[wdth,wght]',
  fallbackFontWeight = 400,
  fallbackFontStretch = 100,
  fallbackFontStyle = null,
}) {
  // 1) Ensure designsData is always an array (convert if needed)
  const isSingleDesign = !Array.isArray(designsData);
  const designsArray = isSingleDesign ? [designsData] : designsData;

  if (!designsArray || designsArray.length === 0) {
    return {
      sanitizedDesignsData: isSingleDesign ? null : [],
      replacedFontsSummary: isSingleDesign ? null : [],
    };
  }

  // 2) Setup fallback font weight/stretch constraints
  const MIN_WEIGHT = 100;
  const MAX_WEIGHT = 900;
  const MIN_STRETCH = 62.5;
  const MAX_STRETCH = 100;

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val)); // Helper to clamp a numeric value

  // 3) Collect used fonts
  const usedFontFamilies = new Set();
  designsArray.forEach((design) => {
    design.keycapData.forEach((row) => {
      row.forEach((keycap) => {
        if (!keycap.isSpacer && keycap.texts) {
          for (const textObj of Object.values(keycap.texts)) {
            if (textObj.fontFamilyName) {
              usedFontFamilies.add(textObj.fontFamilyName);
            }
          }
        }
      });
    });
  });

  // 4) Check which fonts are active
  let fontActivityMap = {};
  if (usedFontFamilies.size > 0) {
    try {
      const { data } = await api.post(
        '/api/fonts/check-activity',
        { fontFamilies: [...usedFontFamilies] } // body
      );
      fontActivityMap = data.fontMap; // always sent by the route; an answer without one fails the loop below rather than replacing every font, as the empty map it fell back to until backfill PR 27 did
    } catch (error) {
      console.error('Error fetching font activity:', error);
      return {
        sanitizedDesignsData: designsData,
        replacedFontsSummary: isSingleDesign ? null : [],
      }; // the fonts stay as they are: an empty map here replaced every font with the fallback and deleted the texts it couldn't show, and the customizer wrote that back to the browser's storage over a passing failure (fixed in backfill PR 27)
    }
  }

  // 5) Fetch & parse the fallback TTF (we do this so we can check for unsupported glyphs), only when a text below gets it
  let fallbackFontKit = null;
  const anyInactive = [...usedFontFamilies].some(
    (family) => fontActivityMap[family] !== true
  ); // the loop below replaces every font the map doesn't mark active (until backfill PR 27 the fallback was downloaded and parsed at every design load, whether a text needed it or not); the guard assumes every text names a family, which TextEditor has written since Nov 2024 and the migrations don't touch, so the two agree
  if (anyInactive) {
    try {
      const ttfResponse = await api.get(
        `/api/fonts/ttf/${fallbackFontFamilyName}?fontName=${fallbackFontName}`,
        {
          responseType: 'json',
        }
      );

      if (!ttfResponse.data.ttfUrl) {
        console.warn(
          'No ttfUrl found for fallback font:',
          fallbackFontFamilyName,
          fallbackFontName
        );
      } else {
        // Now fetch the actual TTF file
        const fontFileResp = await fetch(ttfResponse.data.ttfUrl);
        const arrayBuffer = await fontFileResp.arrayBuffer();
        fallbackFontKit = create(new Uint8Array(arrayBuffer));
      }
    } catch (err) {
      console.error('Error fetching fallback font TTF:', err);
    }
  }

  // 5) Replace all inactive or missing fonts & remove text if fallback lacks glyphs
  const replacedFontsSummary = [];
  const sanitizedDesignsData = designsArray.map((design) => {
    const replacedKeycaps = [];

    const newKeycapData = design.keycapData.map((row, rowIndex) => {
      let colCounter = 0;
      const displayRowIndex = rowIndex + 1; // 1-based row counting

      return row.map((keycap) => {
        if (!keycap.isSpacer) {
          colCounter += 1; // If spacer, do NOT increment colCounter (1-based column counting for non-spacer)
        }

        if (!keycap.isSpacer && keycap.texts) {
          const replacedTextsForThisKeycap = [];

          for (const [textKey, textObj] of Object.entries(keycap.texts)) {
            const currentFamily = textObj.fontFamilyName;
            const isActive = fontActivityMap[currentFamily];

            if (isActive !== true) {
              const textValue = textObj.text || '';
              const replacedTextInfo = {
                originalFontFamily: currentFamily,
                textValue,
                wasDeleted: false, // We'll set to true if we remove it
              };

              // Fallback replacement
              textObj.fontFamilyName = fallbackFontFamilyName;
              if (!textObj.style) textObj.style = {};
              textObj.style.fontFamily = fallbackFontName;
              textObj.style.fontStyle = fallbackFontStyle;

              // Clamping fontWeight
              let oldWeight = parseFloat(textObj.style.fontWeight);
              if (Number.isNaN(oldWeight)) {
                oldWeight = fallbackFontWeight;
              }
              let clampedWeight = clamp(oldWeight, MIN_WEIGHT, MAX_WEIGHT); // clamp to [MIN_WEIGHT, MAX_WEIGHT]
              textObj.style.fontWeight = clampedWeight;

              // Clamping fontStretch
              let oldStretch = parseFloat(textObj.style.fontStretch);
              if (Number.isNaN(oldStretch)) {
                oldStretch = fallbackFontStretch; // If there's no valid stretch, use fallback
              }
              let clampedStretch = clamp(oldStretch, MIN_STRETCH, MAX_STRETCH); // clamp to [MIN_STRETCH, MAX_STRETCH]
              textObj.style.fontStretch = clampedStretch;

              // Check for unsupported glyphs in the fallback font
              if (fallbackFontKit) {
                const unsupported = checkUnsupportedGlyphs(
                  fallbackFontKit,
                  textValue
                );
                if (unsupported.length > 0) {
                  replacedTextInfo.wasDeleted = true;

                  delete keycap.texts[textKey]; // **Remove** the text entry entirely
                }
              }

              replacedTextsForThisKeycap.push(replacedTextInfo);
            }
          }

          if (replacedTextsForThisKeycap.length > 0) {
            replacedKeycaps.push({
              rowIndex: displayRowIndex,
              colIndex: colCounter,
              replacedTexts: replacedTextsForThisKeycap,
            });
          }
        }
        return keycap;
      });
    });

    if (replacedKeycaps.length > 0) {
      replacedFontsSummary.push({
        designId: design._id,
        designName: design.name,
        replacedKeycaps,
      });
    }

    return { ...design, keycapData: newKeycapData };
  });

  // 4) Return correct format based on input type
  return {
    sanitizedDesignsData: isSingleDesign
      ? sanitizedDesignsData[0]
      : sanitizedDesignsData,
    replacedFontsSummary: isSingleDesign
      ? replacedFontsSummary[0] || null
      : replacedFontsSummary,
  };
}

export async function purgeCustomLayoutsFromStorage() {
  try {
    // 1) Remove the design_state from the users localStorage, if it is based on a restricted product (custom layout):
    const rawState = localStorage.getItem('design_state'); // get the design_state from the users localStorage

    if (rawState) {
      try {
        const state = JSON.parse(rawState);
        if (state?.metadata?.isCustomLayout === true) {
          localStorage.removeItem('design_state'); // remove the design_state from the localStorage, if metadata.isCustomLayout is true
        }
      } catch {
        localStorage.removeItem('design_state'); // if the JSON is malformed -> safest is to remove the design_state
      }
    }

    // 2) Remove items from cartItems in the users localStorage (+ their IndexedDB payloads) if they are based on restricted products (custom layouts).
    // Also drop items that reference a designId whose design is not 'public':
    const rawCart = localStorage.getItem('cartItems'); // get the cartItems from the localStorage

    if (rawCart) {
      try {
        const cart = JSON.parse(rawCart) || [];

        const designIdsToCheck = Array.from(
          new Set(
            cart.filter((it) => it?.designId).map((it) => String(it.designId))
          )
        ); // collect all designIds from all cart items

        // 2.1 Build a Set of designIds that are confirmed 'public':
        let publicDesignIds = new Set();
        if (designIdsToCheck.length > 0) {
          try {
            const { data } = await api.post('/api/designs/public-status', {
              ids: designIdsToCheck,
            }); // check the publicStatus for all the designs in the database

            publicDesignIds = new Set(
              (Array.isArray(data) ? data : [])
                .filter((d) => d?.publicStatus === 'public')
                .map((d) => String(d._id))
            ); // get all the public designs
          } catch {
            publicDesignIds = new Set(); // on failure we cannot confirm any as public; default to removing all designId-based items.
          }
        }

        const keep = []; // items to persist
        const removals = []; // uniqueCartIds to purge from IndexedDB

        for (const item of cart) {
          // go through all cart items:
          if (item?.isCustomLayout === true) {
            if (item?.uniqueCartId) removals.push(item.uniqueCartId); // if the item has a uniqueCartId and a isCustomLayout field set to true, add it's uniqueCartId to the removals array
            continue; // continue with the next item, as we don't want to push it to the keep array below
          }

          if (item?.designId) {
            // if the current item has a designId:
            const isPublic = publicDesignIds.has(String(item.designId)); // check if the design linked to this designId is public

            if (!isPublic) {
              // if the design of the current cart item is not public:
              continue; // continue with the next item, as we don't want to push it to the keep array below
            }

            keep.push(item); // if the design of the current cart item is public, add the cart item to the keep array
            continue;
          }

          keep.push(item); // for regular cart items (that have no isCustomLayout flag and no designId) -> keep as-is
        }

        await Promise.all(
          removals.map((id) =>
            deleteFromIndexedDB(id).catch(() => {
              // ignore individual failures
            })
          )
        ); // delete the designs, that belong to restricted products in the cart, from the IndexedDB

        localStorage.setItem('cartItems', JSON.stringify(keep)); // update the cartItems in the users localStorage with the filtered cart (keep array)
      } catch {
        localStorage.removeItem('cartItems'); // if cart was corrupted, safest is to clear it entirely
      }
    }
  } catch {
    // don’t block logout on cleanup errors
  }
} // helper function to remove design_state and all cart items from the users localStorage (and indexedDB) if they are based on restricted products (custom layouts). Also drops any cart items that reference a non-public DB design.

export const signOutLocally = async () => {
  localStorage.removeItem('userInfo'); // the signed-in user with their token, as SigninScreen stores it (the Store's USER_SIGNOUT resets its state only: the screens that store these keys remove them)
  localStorage.removeItem('shippingAddress');
  localStorage.removeItem('billingAddress');
  localStorage.removeItem('paymentMethod');

  await purgeCustomLayoutsFromStorage(); // remove design_state and all cart items from the users localStorage (and indexedDB) if they are based on restricted products (custom layouts)
}; // what a sign-out (App.js) and an expired login (api.js) clear on this device: the user and their checkout keys first, then the purge, whose one request (POST /api/designs/public-status) needs no login and which swallows its own failures. App.js's sign-out ran the purge first until chain 10 (Oct 2026); one order now, the login gone first, so a slow purge never leaves a signed-in storage behind: user decision
